import React from 'react';
import {Link, useParams} from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AreasSection from '../components/AreasSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiBuilding, FiCheck, FiPhone, FiCalendar, FiStar, FiUser, FiShield, FiClock} = FiIcons;

const CommunalCleaningPage = () => {
  const {area} = useParams();
  const currentArea = area ? formatAreaName(area) : 'Aylesbury';

  const handleLinkClick = () => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const services = [
    {
      title: "Entrance Halls & Lobbies",
      description: "Professional cleaning of reception areas, maintaining a welcoming first impression for residents and visitors.",
      features: ["Floor cleaning and mopping", "Glass door cleaning", "Reception desk maintenance", "Lighting fixture dusting"]
    },
    {
      title: "Stairwells & Corridors", 
      description: "Thorough cleaning of common walkways, ensuring safe and hygienic passage areas for all residents.",
      features: ["Handrail sanitization", "Floor vacuuming/mopping", "Wall spot cleaning", "Emergency lighting checks"]
    },
    {
      title: "Lift Cleaning",
      description: "Specialized elevator maintenance keeping these high-traffic areas spotless and odor-free.",
      features: ["Interior panel cleaning", "Floor maintenance", "Button sanitization", "Mirror polishing"]
    },
    {
      title: "Communal Kitchens",
      description: "Deep cleaning of shared cooking facilities in retirement homes and assisted living properties.",
      features: ["Appliance cleaning", "Surface sanitization", "Sink and tap cleaning", "Floor deep cleaning"]
    }
  ];

  const benefits = [
    "Improved property value and resident satisfaction",
    "Reduced maintenance costs through regular upkeep",
    "Enhanced health and safety compliance",
    "Professional appearance for property viewings",
    "Reduced complaints from residents and tenants",
    "Extended lifespan of communal facilities"
  ];

  const reviews = [
    {
      name: "Margaret Thompson",
      property: "Riverside Retirement Village",
      rating: 5,
      text: `The communal areas in our retirement property in ${currentArea} have never looked better. Diamond Cleaning's team is reliable, thorough, and always respectful to our residents.`,
      date: "1 week ago"
    },
    {
      name: "David Wilson",
      property: "Greenfield Apartments",
      rating: 5,
      text: `As a property manager in ${currentArea}, I've worked with many cleaning companies. Diamond Cleaning consistently delivers exceptional results in our communal areas.`,
      date: "2 weeks ago"
    },
    {
      name: "Sarah Clarke",
      property: "Meadowview Court",
      rating: 5,
      text: `Our residents constantly compliment the cleanliness of our communal spaces. The team understands the unique needs of retirement properties perfectly.`,
      date: "3 weeks ago"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header currentArea={currentArea} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/img/block.webp')`
          }}
        />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Communal Cleaning Services in {currentArea}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl mx-auto">
              Professional cleaning for apartment buildings, retirement properties, and residential complexes. Creating clean, safe, and welcoming communal spaces for all residents.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
                onClick={handleLinkClick}
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Get Quote Online</span>
              </Link>
              <a
                href="tel:07878522378"
                className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Call: 07878 522378</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Communal Cleaning */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Professional Communal Area Cleaning in {currentArea}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Communal areas are the heart of any residential property. They create the first impression for visitors and significantly impact resident satisfaction. Our specialized communal cleaning services ensure these shared spaces remain pristine, hygienic, and welcoming.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                We understand the unique challenges of maintaining communal areas in apartment buildings, retirement homes, and residential complexes. Our experienced team works discreetly around residents' schedules while delivering consistently high standards.
              </p>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Why Choose Our Communal Cleaning?</h3>
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div 
                className="rounded-2xl overflow-hidden shadow-2xl h-96 bg-cover bg-center"
                style={{backgroundImage: `url('/img/communal-cleaning.webp')`}}
              >
                <div className="absolute inset-0 bg-blue-600 bg-opacity-20"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Communal Cleaning Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive cleaning solutions for all communal areas in residential properties
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {service.description}
                </p>
                <div className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-600" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Communal Areas?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Professional communal cleaning that enhances property value and resident satisfaction in {currentArea}.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiClock} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Flexible Scheduling</h3>
              <p className="text-blue-100">Work around residents' routines</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiShield} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Fully Insured</h3>
              <p className="text-blue-100">Complete peace of mind</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <SafeIcon icon={FiBuilding} className="w-12 h-12 text-white mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Property Specialists</h3>
              <p className="text-blue-100">Expert in residential complexes</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
              onClick={handleLinkClick}
            >
              <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              <span>Get Free Quote</span>
            </Link>
            <a
              href="tel:07878522378"
              className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>Call: 07878 522378</span>
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Property Managers Say About Our Communal Cleaning
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real feedback from satisfied property managers and residents in {currentArea}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <SafeIcon icon={FiUser} className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.name}</h4>
                    <p className="text-sm text-gray-600">{review.property}</p>
                  </div>
                </div>
                
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 mr-2">
                    {[...Array(review.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{review.date}</span>
                </div>
                
                <p className="text-gray-700 leading-relaxed">"{review.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Section */}
      <AreasSection currentArea={currentArea} />

      <Footer />
    </div>
  );
};

const formatAreaName = (area) => {
  return area
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default CommunalCleaningPage;